import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Trash2, Mail, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navbar from '../sections/Navbar';
import Footer from '../sections/Footer';

interface NewsletterEntry {
  email: string;
  date: string;
  source: string;
}

const AdminPage = () => {
  const [emails, setEmails] = useState<NewsletterEntry[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem('newsletter_emails');
    if (stored) {
      setEmails(JSON.parse(stored));
    }
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    // Jednoduché heslo - v produkci by to mělo být bezpečnější
    if (password === 'aiopt2024') {
      setIsAuthenticated(true);
    } else {
      alert('Nesprávné heslo');
    }
  };

  const exportToCSV = () => {
    const csv = [
      ['Email', 'Datum přihlášení', 'Zdroj'].join(','),
      ...emails.map(e => [e.email, new Date(e.date).toLocaleString('cs-CZ'), e.source].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `newsletter-emails-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportToJSON = () => {
    const json = JSON.stringify(emails, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `newsletter-emails-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const clearAll = () => {
    if (confirm('Opravdu chcete smazat všechny emaily?')) {
      localStorage.removeItem('newsletter_emails');
      setEmails([]);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50">
        <Navbar />
        <main className="relative z-10 pt-24 pb-16">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="max-w-md mx-auto">
              <Link to="/">
                <Button variant="ghost" className="mb-6 text-slate-500 -ml-4">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zpět
                </Button>
              </Link>

              <div className="glass-card rounded-2xl p-8">
                <h1 className="text-2xl font-bold mb-6 text-slate-800 text-center">Admin přístup</h1>
                <form onSubmit={handleLogin}>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Heslo"
                    className="w-full px-4 py-3 rounded-lg bg-white/50 border border-slate-200 text-slate-800 mb-4"
                  />
                  <Button type="submit" className="w-full bg-gradient-to-r from-[#0ea5e9] to-[#00d4aa] text-white">
                    Přihlásit
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50">
      <Navbar />
      
      <main className="relative z-10 pt-24 pb-16">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Link to="/">
              <Button variant="ghost" className="mb-6 text-slate-500 -ml-4">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zpět
              </Button>
            </Link>

            <div className="glass-card rounded-2xl p-6 md:p-8">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-slate-800">Newsletter přihlášení</h1>
                  <p className="text-slate-500">Seznam všech přihlášených emailů</p>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#0ea5e9]" />
                  <span className="text-2xl font-bold text-slate-800">{emails.length}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3 mb-6">
                <Button
                  onClick={exportToCSV}
                  variant="outline"
                  className="border-slate-300"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button
                  onClick={exportToJSON}
                  variant="outline"
                  className="border-slate-300"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export JSON
                </Button>
                <Button
                  onClick={clearAll}
                  variant="outline"
                  className="border-red-300 text-red-500 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Smazat vše
                </Button>
              </div>

              {/* Email List */}
              {emails.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Zatím žádné přihlášené emaily</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-slate-700 font-semibold">Email</th>
                        <th className="text-left py-3 px-4 text-slate-700 font-semibold">Datum</th>
                        <th className="text-left py-3 px-4 text-slate-700 font-semibold">Zdroj</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emails.map((entry, index) => (
                        <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4 text-slate-600">{entry.email}</td>
                          <td className="py-3 px-4 text-slate-500 text-sm">
                            {new Date(entry.date).toLocaleString('cs-CZ')}
                          </td>
                          <td className="py-3 px-4 text-slate-500 text-sm truncate max-w-xs">
                            {entry.source}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AdminPage;
